import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, KeyRound, LockKeyhole, MessageCircle, RotateCw, UserPlus } from "lucide-react";
import { PageTitle } from "@/components/SettingsPanel";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import { useLive } from "@/lib/live";


type Pending = { user_id: string; name: string; phone: string; created_at: string; code: string | null; expires_at: string | null; attempts: number };
type Reset = { user_id: string; name: string; phone: string; code: string | null; attempts: number; created_at: string };
type Temp = { user_id: string; name: string; booking_name?: string; phone: string; temp_password_plain: string };
const wa = (p: string, m: string) => `https://wa.me/${p.replace(/\D/g, "")}?text=${encodeURIComponent(m)}`;
const pill = "h-9 px-3 rounded-full text-xs font-bold inline-flex items-center gap-1.5";

export default function AdminActivations() {
  const { t } = usePrefs();
  const [d, setD] = useState<{ pending: Pending[]; temp_passwords: Temp[]; resets: Reset[] } | null>(null);
  const load = () => api.get("/admin/activations").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useLive(load);
  const renew = async (id: string) => {
    try { await api.post(`/admin/activations/${id}/renew`); toast.success(t("act.renewed")); load(); } catch (e) { toast.error(errorText(e, t)); }
  };
  const renewReset = async (id: string) => {
    try { await api.post(`/admin/activations/${id}/renew-reset`); toast.success(t("act.renewed")); load(); } catch (e) { toast.error(errorText(e, t)); }
  };
  const copy = (s: string) => navigator.clipboard?.writeText(s).then(() => toast.success(t("sec.copied"))).catch(() => {});
  const site = window.location.origin;

  return (
    <div data-testid="admin-activations-page" className="space-y-6">
      <PageTitle title={t("act.title")} sub={t("act.sub")} />
      <section className="space-y-2">
        <h2 className="font-bold inline-flex items-center gap-2"><UserPlus className="h-5 w-5 text-primary" />{t("act.pending")} <span className="text-muted-foreground text-sm">({d?.pending.length ?? 0})</span></h2>
        {d && !d.pending.length && <p data-testid="act-empty" className="rounded-2xl border bg-card p-4 text-sm text-muted-foreground">{t("act.none")}</p>}
        <div className="grid gap-2 lg:grid-cols-2">
          {d?.pending.map((p) => {
            const msg = t("act.msg").replace("{name}", p.name).replace("{code}", p.code || "").replace("{site}", site);
            return (
              <div key={p.user_id} data-testid={`act-row-${p.user_id}`} className="rounded-2xl border bg-card p-3 space-y-2">
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-bold truncate">{p.name}</p>
                    <p className="text-xs text-muted-foreground" dir="ltr">{p.phone}</p>
                  </div>
                  {p.code ? <button onClick={() => copy(p.code!)} data-testid={`act-code-${p.user_id}`} dir="ltr" className="font-display text-2xl font-black tracking-widest text-primary">{p.code}</button>
                    : <span data-testid={`act-expired-${p.user_id}`} className="text-xs font-bold text-amber-600">{t("act.expired")}</span>}
                </div>
                <p className="text-[11px] text-muted-foreground">{p.code ? "" : t("act.needNew")}{p.attempts ? `${p.code ? "" : " · "}${t("act.attempts").replace("{n}", String(p.attempts))}` : ""}</p>
                <div className="flex gap-2">
                  {p.code && <a href={wa(p.phone, msg)} target="_blank" rel="noreferrer" data-testid={`act-wa-${p.user_id}`} className={`${pill} bg-[#25D366] text-white flex-1 justify-center`}><MessageCircle className="h-4 w-4" />{t("act.send")}</a>}
                  <button onClick={() => renew(p.user_id)} data-testid={`act-renew-${p.user_id}`} className={`${pill} border`}><RotateCw className="h-3.5 w-3.5" />{t("act.renew")}</button>
                </div>
              </div>
            );
          })}
        </div>
      </section>
      <section className="space-y-2" data-testid="act-reset-section">
        <h2 className="font-bold inline-flex items-center gap-2"><LockKeyhole className="h-5 w-5 text-primary" />{t("act.resets")} <span className="text-muted-foreground text-sm">({d?.resets.length ?? 0})</span></h2>
        {d && !d.resets.length && <p data-testid="act-reset-empty" className="rounded-2xl border bg-card p-4 text-sm text-muted-foreground">{t("act.resetsNone")}</p>}
        <div className="grid gap-2 lg:grid-cols-2">
          {d?.resets.map((r) => {
            const msg = t("act.resetMsg").replace("{name}", r.name).replace("{code}", r.code || "").replace("{site}", site);
            return (
              <div key={r.user_id} data-testid={`act-reset-${r.user_id}`} className="rounded-2xl border bg-card p-3 space-y-2">
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-bold truncate">{r.name}</p>
                    <p className="text-xs text-muted-foreground" dir="ltr">{r.phone}</p>
                  </div>
                  {r.code ? <button onClick={() => copy(r.code!)} data-testid={`act-reset-code-${r.user_id}`} dir="ltr" className="font-display text-2xl font-black tracking-widest text-primary">{r.code}</button>
                    : <span data-testid={`act-reset-locked-${r.user_id}`} className="text-xs font-bold text-amber-600">{t("act.locked")}</span>}
                </div>
                {!!r.attempts && <p className="text-[11px] text-muted-foreground">{t("act.attempts").replace("{n}", String(r.attempts))}</p>}
                <div className="flex gap-2">
                  {r.code && <a href={wa(r.phone, msg)} target="_blank" rel="noreferrer" data-testid={`act-reset-wa-${r.user_id}`} className={`${pill} bg-[#25D366] text-white flex-1 justify-center`}><MessageCircle className="h-4 w-4" />{t("act.send")}</a>}
                  <button onClick={() => renewReset(r.user_id)} data-testid={`act-reset-renew-${r.user_id}`} className={`${pill} border`}><RotateCw className="h-3.5 w-3.5" />{t("act.renew")}</button>
                </div>
              </div>
            );
          })}
        </div>
      </section>
      {!!d?.temp_passwords.length && (
        <section className="space-y-2" data-testid="act-temp-section">
          <h2 className="font-bold inline-flex items-center gap-2"><KeyRound className="h-5 w-5 text-primary" />{t("act.temps")} <span className="text-muted-foreground text-sm">({d.temp_passwords.length})</span></h2>
          <p className="text-xs text-muted-foreground">{t("act.tempsNote")}</p>
          <div className="grid gap-2 lg:grid-cols-2">
            {d.temp_passwords.map((u) => {
              const msg = t("act.tempMsg").replace("{name}", u.booking_name || u.name).replace("{phone}", u.phone).replace("{pw}", u.temp_password_plain).replace("{site}", site);
              return (
                <div key={u.user_id} data-testid={`act-temp-${u.user_id}`} className="rounded-2xl border bg-card p-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-bold truncate">{u.booking_name || u.name}</p>
                    <p className="text-xs text-muted-foreground" dir="ltr">{u.phone} · <button onClick={() => copy(u.temp_password_plain)} className="font-bold text-foreground inline-flex items-center gap-1" data-testid={`act-temp-pw-${u.user_id}`}>{u.temp_password_plain}<Copy className="h-3 w-3" /></button></p>
                  </div>
                  <a href={wa(u.phone, msg)} target="_blank" rel="noreferrer" data-testid={`act-temp-wa-${u.user_id}`} className={`${pill} bg-[#25D366] text-white`}><MessageCircle className="h-4 w-4" />{t("act.send")}</a>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
